import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Typography,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const ProductEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/products",
    token
  );
  const { data: categoriesData, loading: catLoading } = useFetchObjects(
    ["category"],
    "shop/categories",
    token
  );

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const categories = Array.isArray(categoriesData) ? categoriesData : categoriesData?.results || [];
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [pack, setPack] = useState("1x1");
  const [carton, setCarton] = useState(1);
  const [raf, setRaf] = useState("");
  const [brandName, setBrandName] = useState("");

  // Set basic fields when editData changes.
  useEffect(() => {
    if (editData) {
      setName(editData.name);
      setDescription(editData.description || "");
      setPack(editData.pack || "1x1");
      setCarton(editData.carton || 1);
      setRaf(editData.raf || "");
      setBrandName(editData.brand_name || "");
      // Determine category id from editData:
      // If editData.category is an object, use its id; otherwise use editData.category directly.
      const catId =
        editData.category && editData.category.id
          ? String(editData.category.id)
          : editData.category
          ? String(editData.category)
          : "";
      setSelectedCategory(catId);

      const shopId =
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : "";
      setSelectedShop(shopId);
    }
  }, [editData]);

  // If categories are loaded and editData exists, ensure the selected category is valid.
  useEffect(() => {
    if (editData && categories && categories.length > 0) {
      const catId =
        editData.category && editData.category.id
          ? String(editData.category.id)
          : editData.category
          ? String(editData.category)
          : "";
      const exists = categories.find((cat) => String(cat.id) === catId);
      if (exists) {
        setSelectedCategory(catId);
      } else {
        setSelectedCategory("");
      }
    }
  }, [editData, categories]);

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("description", description);
    formData.append("category", selectedCategory);
    formData.append("pack", pack);
    formData.append("carton", carton);
    formData.append("raf", raf.trim());
    formData.append("brand_name", brandName.trim());
    if (imageFile) {
      formData.append("image", imageFile);
    }
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setName("");
      setDescription("");
      setSelectedCategory("");
      setImageFile(null);
      setPack("1x1");
      setCarton(1);
      setRaf("");
      setBrandName("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("product.editProduct")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("product.productName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("product.description")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label={t("product.selectCategory")}
            fullWidth
            required
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {catLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              categories.map((cat) => (
                <MenuItem key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("customer.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("product.pack")}
            type="text"
            fullWidth
            value={pack}
            onChange={(e) => setPack(e.target.value)}
            placeholder="e.g., 1x1"
          />
          <TextField
            margin="dense"
            label={t("product.carton")}
            type="number"
            fullWidth
            value={carton}
            onChange={(e) => setCarton(parseInt(e.target.value) || 1)}
            inputProps={{ min: 1 }}
          />
          <TextField
            margin="dense"
            label={t("product.raf")}
            type="text"
            fullWidth
            value={raf}
            onChange={(e) => setRaf(e.target.value)}
          />
         
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" component="label">
              {t("product.uploadImage")}
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
            {imageFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {imageFile.name}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("product.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim() || !selectedCategory}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("product.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductEdit;
